/**
 * Canonical requisitions workflow routing metadata.
 * Keep this as the single source of truth for route segments and labels
 * used by dashboard/tasks/repository pages and shared workflow links.
 */

export const REQUISITION_WORKFLOW_PATHS = {
  internal_memo: "internal-memo",
  imprest: "imprest",
  imprest_retirement: "retirement",
  payment_voucher: "payment-voucher",
  petty_cash_voucher_hq: "petty-cash-hq",
  petty_cash_voucher_arusha: "petty-cash-arusha",
  petty_cash_voucher_dodoma: "petty-cash-dodoma",
  petty_cash_voucher_kinondoni: "petty-cash-kinondoni",
  claim: "claim",
  invoice: "invoices",
  normal_mail: "normal-mail",
  job_application: "job-applications",
  volunteer_allowance: "volunteer-allowance",
  activity_report: "activity-report",
  /** Detail/edit: Procurement Memo app (not the procurement queue list segment). */
  procurement_requisition: "procurement-memo",
};

export const PETTY_CASH_VARIANT_PATHS = {
  hq: "petty-cash-hq",
  arusha: "petty-cash-arusha",
  dodoma: "petty-cash-dodoma",
  kinondoni: "petty-cash-kinondoni",
};

export const PETTY_CASH_VARIANT_LABELS = {
  hq: "Petty Cash (HQ)",
  arusha: "Petty Cash (Arusha)",
  dodoma: "Petty Cash (Dodoma)",
  kinondoni: "Petty Cash (Kinondoni)",
};

export const PETTY_CASH_VARIANT_WORKFLOW_TYPES = {
  hq: "petty_cash_voucher_hq",
  arusha: "petty_cash_voucher_arusha",
  dodoma: "petty_cash_voucher_dodoma",
  kinondoni: "petty_cash_voucher_kinondoni",
};

export const PETTY_CASH_WORKFLOW_TYPES = Object.values(
  PETTY_CASH_VARIANT_WORKFLOW_TYPES
);

export const REQUISITION_WORKFLOW_TYPES = [
  "internal_memo",
  "imprest",
  "imprest_retirement",
  "payment_voucher",
  ...PETTY_CASH_WORKFLOW_TYPES,
  "claim",
  "invoice",
  "normal_mail",
  "job_application",
  "volunteer_allowance",
  "activity_report",
  "procurement_requisition",
];

export const REQUISITION_WORKFLOW_LABELS = {
  internal_memo: "Internal Memo",
  imprest: "Imprest Request",
  imprest_retirement: "Imprest Retirement",
  payment_voucher: "Payment Voucher",
  petty_cash_voucher_hq: "Petty Cash (HQ)",
  petty_cash_voucher_arusha: "Petty Cash (Arusha)",
  petty_cash_voucher_dodoma: "Petty Cash (Dodoma)",
  petty_cash_voucher_kinondoni: "Petty Cash (Kinondoni)",
  claim: "Claim",
  invoice: "Invoice",
  normal_mail: "Normal Mails",
  job_application: "Job Applications",
  volunteer_allowance: "Volunteer Allowance",
  activity_report: "Activity Report",
  procurement_requisition: "Procurement requisition",
};

export function getRequisitionWorkflowRouteSegment(workflowType) {
  if (!workflowType) return null;
  return REQUISITION_WORKFLOW_PATHS[workflowType] ?? null;
}

export function inferPettyCashVariantKey(definitionName = "") {
  const normalized = String(definitionName).trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("petty_cash_voucher_kinondoni")) return "kinondoni";
  if (normalized.includes("petty_cash_voucher_arusha")) return "arusha";
  if (normalized.includes("petty_cash_voucher_dodoma")) return "dodoma";
  if (normalized.includes("petty_cash_voucher_hq")) return "hq";
  if (normalized.includes("kinondoni")) return "kinondoni";
  if (normalized.includes("arusha")) return "arusha";
  if (normalized.includes("dodoma")) return "dodoma";
  if (normalized.includes("hq")) return "hq";
  return null;
}

export function getPettyCashVariantKeyFromWorkflowType(workflowType) {
  return (
    Object.entries(PETTY_CASH_VARIANT_WORKFLOW_TYPES).find(
      ([, type]) => type === workflowType
    )?.[0] ?? null
  );
}

export function getPettyCashWorkflowType(variantKey) {
  if (!variantKey) return null;
  return PETTY_CASH_VARIANT_WORKFLOW_TYPES[variantKey] ?? null;
}

export function getPettyCashRouteSegment(variantKey) {
  if (!variantKey) return null;
  return PETTY_CASH_VARIANT_PATHS[variantKey] ?? null;
}

function resolveWorkflowTypeAndVariant(workflowOrType) {
  if (!workflowOrType) {
    return { workflowType: null, variantKey: null };
  }

  if (typeof workflowOrType === "string") {
    return {
      workflowType: workflowOrType,
      variantKey: getPettyCashVariantKeyFromWorkflowType(workflowOrType),
    };
  }

  const workflowType =
    workflowOrType?.workflowType ??
    workflowOrType?.workflowDefinition?.workflowType ??
    null;
  const definitionName =
    workflowOrType?.workflowDefinition?.name ??
    workflowOrType?.workflowDefinitionName ??
    workflowOrType?.definitionName ??
    "";

  return {
    workflowType,
    variantKey: PETTY_CASH_WORKFLOW_TYPES.includes(workflowType)
      ? getPettyCashVariantKeyFromWorkflowType(workflowType) ??
        inferPettyCashVariantKey(definitionName)
      : null,
  };
}

export function getRelativeRequisitionWorkflowPath(workflowOrType, workflowId) {
  const { workflowType, variantKey } = resolveWorkflowTypeAndVariant(workflowOrType);
  const row =
    workflowOrType && typeof workflowOrType === 'object' && !Array.isArray(workflowOrType)
      ? workflowOrType
      : null;

  if (workflowType === 'procurement_requisition' && row) {
    const parentRaw =
      row.parentInstanceId ?? row.parentInstanceID ?? row.parent?._id ?? row.parent?.id ?? null;
    if (parentRaw != null && String(parentRaw).trim() !== '') {
      return `internal-memo/${String(parentRaw)}`;
    }
  }

  /** Incoming mail engine type is always `normal_mail`; category is disambiguated by flags. */
  if (workflowType === "normal_mail" && row) {
    const job =
      row.isJobApplication === true ||
      row.formData?.isJobApplication === true;
    const inv =
      row.isInvoice === true || row.formData?.isInvoice === true;
    if (job) {
      return workflowId ? `job-applications/${workflowId}` : "job-applications";
    }
    if (inv) {
      return workflowId ? `invoice-mail/${workflowId}` : "invoice-mail";
    }
    return workflowId ? `normal-mail/${workflowId}` : "normal-mail";
  }

  const segment =
    PETTY_CASH_WORKFLOW_TYPES.includes(workflowType)
      ? getPettyCashRouteSegment(variantKey)
      : getRequisitionWorkflowRouteSegment(workflowType);
  if (!segment) return null;
  return workflowId ? `${segment}/${workflowId}` : segment;
}

export function getAbsoluteRequisitionWorkflowPath(workflowOrType, workflowId) {
  const relativePath = getRelativeRequisitionWorkflowPath(
    workflowOrType,
    workflowId
  );
  return relativePath ? `/app/requisitions/${relativePath}` : "/app/requisitions";
}

export function getRequisitionWorkflowLabel(workflowType) {
  if (!workflowType) return "Unknown";
  return REQUISITION_WORKFLOW_LABELS[workflowType] ?? workflowType;
}
